<template>
  <div class="iso-templates-page">
    <!-- ===================== Hero ===================== -->
    <div class="iso-hero">
      <div class="iso-hero__blob iso-hero__blob--one" />
      <div class="iso-hero__blob iso-hero__blob--two" />

      <div class="iso-hero__content">
        <div class="iso-hero__icon">
          <feather-icon
            icon="HashIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="iso-hero__title">
            ISO Scope
          </h2>
          <p class="iso-hero__subtitle">
            Browse ISO certificate groups and their linked scope .
          </p>
        </div>
      </div>
    </div>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="iso-toolbar-card mb-1"
    >
      <div class="iso-toolbar">
        <div class="iso-toolbar__title">
          <b-avatar
            rounded
            size="40"
            class="mr-1 iso-toolbar__avatar"
          >
            <feather-icon
              icon="LayersIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              Scope
            </h4>
            <small class="text-muted">Certificate groups and their ISO scopes</small>
          </div>
        </div>

        <b-input-group class="iso-search">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            placeholder="Search certificate or scope..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="searchQuery"
            is-text
            class="iso-search__clear"
            @click="searchQuery = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-skel">
          <div class="iso-skel__accent" />
          <div class="iso-skel__head">
            <div class="iso-skel__avatar" />
            <div class="iso-skel__lines">
              <div class="iso-skel__line iso-skel__line--short" />
              <div class="iso-skel__line iso-skel__line--long" />
            </div>
          </div>
          <div class="iso-skel__body">
            <div class="iso-skel__chip" />
            <div class="iso-skel__chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty ===================== -->
    <b-card
      v-else-if="!filteredRows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No scope found</h4>
      <p class="text-muted mb-0">
        {{ searchQuery ? 'Try adjusting your search.' : 'There are no ISO scopes to display yet.' }}
      </p>
    </b-card>

    <!-- ===================== Cards ===================== -->
    <b-row
      v-else
      class="iso-grid"
    >
      <b-col
        v-for="row in filteredRows"
        :key="row.certificate_group_id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-card">
          <div
            class="iso-card__accent"
            :style="{ background: getGradient(row) }"
          />

          <div class="iso-card__head">
            <div
              class="iso-card__avatar"
              :style="{ background: getGradient(row) }"
            >
              <feather-icon
                :icon="getIcon(row)"
                size="22"
              />
            </div>
            <div class="iso-card__head-text">
              <h4
                class="iso-card__name"
                :title="row.certificate_group_name"
              >
                {{ row.certificate_group_name || 'Untitled certificate' }}
              </h4>
              <span class="iso-card__id">#{{ row.certificate_group_id }}</span>
            </div>
            <span class="iso-card__source">
              <feather-icon
                icon="ShieldIcon"
                size="12"
                class="mr-25"
              />
              ISO
            </span>
          </div>

          <div class="iso-card__body">
            <div class="iso-card__section-label">
              <feather-icon
                icon="TagIcon"
                size="14"
                class="mr-25"
              />
              scope
            </div>

            <div class="iso-engineers">
              <span
                class="iso-engineer-chip iso-scope-chip"
                :title="row.scope_name"
              >
                <span
                  class="iso-engineer-chip__dot"
                  :style="{ background: getGradient(row) }"
                >
                  <feather-icon
                    icon="HashIcon"
                    size="11"
                  />
                </span>
                {{ row.scope_name || '—' }}
              </span>
            </div>
          </div>

          <div class="iso-card__footer">
            <button
              type="button"
              class="iso-edit-btn"
              @click="goToEdit(row)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              code
            </button>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BRow, BCol, BCard, BAvatar,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const SCOPE_ICON_RULES = [
  { keys: ['9001', 'qhse', 'qms'], icon: 'CheckCircleIcon' },
  { keys: ['22000', 'fsms', 'food'], icon: 'CoffeeIcon' },
  { keys: ['integrated', 'ims'], icon: 'LayersIcon' },
  { keys: ['14001', 'environ', 'ems'], icon: 'CloudIcon' },
  { keys: ['45001', 'safety', 'ohsas'], icon: 'ShieldIcon' },
  { keys: ['27001', 'security', 'isms'], icon: 'LockIcon' },
  { keys: ['50001', 'energy'], icon: 'ZapIcon' },
]

const SCOPE_ICON_FALLBACK = ['HashIcon', 'BookmarkIcon', 'AwardIcon', 'FileTextIcon']

const SCOPE_GRADIENTS = [
  'linear-gradient(135deg, #0d9488, #2dd4bf)',
  'linear-gradient(135deg, #0891b2, #22d3ee)',
  'linear-gradient(135deg, #059669, #34d399)',
  'linear-gradient(135deg, #0e7490, #67e8f9)',
  'linear-gradient(135deg, #14b8a6, #5eead4)',
  'linear-gradient(135deg, #0284c7, #38bdf8)',
]

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BAvatar,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      scopes: [],
      dataLoad: false,
      searchQuery: '',
    }
  },
  computed: {
    filteredRows() {
      const term = this.searchQuery.trim().toLowerCase()
      if (!term) return this.scopes

      return this.scopes.filter(row => {
        const certName = (row.certificate_group_name || '').toLowerCase()
        const scopeName = (row.scope_name || '').toLowerCase()
        const id = String(row.certificate_group_id || '')
        return certName.includes(term) || scopeName.includes(term) || id.includes(term)
      })
    },
  },
  mounted() {
    this.fetchScopes()
  },
  methods: {
    async fetchScopes() {
      this.dataLoad = true

      try {
        const response = await axios.get('iso/scopes')

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data
          this.scopes = Array.isArray(data) ? data : []
        }
      } catch (error) {
        if (error.response?.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to load ISO scopes',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },

    getIcon(row) {
      const haystack = `${row.certificate_group_name || ''} ${row.scope_name || ''}`.toLowerCase()
      const rule = SCOPE_ICON_RULES.find(({ keys }) => keys.some(key => haystack.includes(key)))
      if (rule) return rule.icon
      return SCOPE_ICON_FALLBACK[(row.certificate_group_id || 0) % SCOPE_ICON_FALLBACK.length]
    },

    getGradient(row) {
      return SCOPE_GRADIENTS[Math.abs(Number(row.certificate_group_id) || 0) % SCOPE_GRADIENTS.length]
    },

    goToEdit(row) {
      this.$router.push({
        name: 'edit-iso-scope-codes',
        params: { id: row.certificate_group_id },
      })
    },
  },
}
</script>

<style lang="scss" scoped src="../IsoTemplates.scss"></style>

<style lang="scss" scoped>
.iso-scope-chip {
  font-size: 0.85rem !important;
  padding: 6px 12px 6px 6px !important;
}
</style>
